import { useEffect, useRef, useState, useCallback } from 'react';
import { useChat, sessionHeaders, RETRY_BUTTON_VALUE } from '../hooks/useChat';
import { ChatMessage } from './ChatMessage';
import { ChatInput } from './ChatInput';
import { TypingIndicator } from './TypingIndicator';

const API_URL = import.meta.env.VITE_API_URL || '';

export function Chat() {
  const {
    messages,
    sessionId,
    sessionToken,
    isLoading,
    startError,
    isTesterMode,
    currentInputType,
    currentPlaceholder,
    currentButtons,
    startChat,
    sendMessage,
    sendButtonClick,
    retryLastRequest,
  } = useChat();

  const messagesRef = useRef<HTMLDivElement>(null);
  const [testerComments, setTesterComments] = useState<Record<string, string>>({});
  const [testerError, setTesterError] = useState<string | null>(null);

  // Kommentare laden wenn Session existiert und Tester-Modus aktiv
  useEffect(() => {
    if (!isTesterMode || !sessionId) return;
    fetch(`${API_URL}/api/chat/comments/${sessionId}`, { headers: sessionHeaders(sessionToken) })
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(data => setTesterComments(data.comments || {}))
      .catch(() => setTesterError('Kommentare konnten nicht geladen werden.'));
  }, [isTesterMode, sessionId, sessionToken]);

  const handleTesterComment = useCallback(async (messageIndex: number, kommentar: string) => {
    if (!sessionId) return;
    try {
      const res = await fetch(`${API_URL}/api/chat/comment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...sessionHeaders(sessionToken) },
        body: JSON.stringify({ sessionId, messageIndex, kommentar }),
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const data = await res.json();
      if (data.comments) {
        setTesterComments(data.comments);
      }
      setTesterError(null);
    } catch (e) {
      console.error('Failed to save tester comment:', e);
      setTesterError('Der Kommentar konnte nicht gespeichert werden. Bitte versuchen Sie es erneut.');
    }
  }, [sessionId, sessionToken]);

  // Den Nachrichten-Container direkt scrollen, NICHT per scrollIntoView.
  // scrollIntoView würde sämtliche scrollbaren Vorfahren mitscrollen - im
  // Iframe also auch die einbettende Seite, die dann bei jeder Nachricht springt.
  useEffect(() => {
    const el = messagesRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [messages, isLoading]);

  const handleButtonClick = (value: string, label: string) => {
    if (value === RETRY_BUTTON_VALUE) {
      retryLastRequest();
      return;
    }
    if (value === 'neu') {
      window.location.reload();
      return;
    }
    // noopener: die geöffnete Seite bekommt sonst über window.opener Zugriff auf das Chat-Fenster
    if (value === 'upsell' || value === 'auskunft') {
      window.open('https://werbeanlage-baugenehmigung.de/bauplanungsrechtliche-pruefung/', '_blank', 'noopener,noreferrer');
    }
    if (value === 'blog_folgen') {
      window.open('https://werbeanlage-baugenehmigung.de/werbeanlage-ohne-baugenehmigung/', '_blank', 'noopener,noreferrer');
    }
    if (value === 'blog_verfahrensfrei') {
      window.open('https://werbeanlage-baugenehmigung.de/verfahrensfreie-werbeanlage-was-ist-trotzdem-zu-beachten/', '_blank', 'noopener,noreferrer');
    }
    sendButtonClick(value, label);
  };

  if (messages.length === 0) {
    const startLabel = startError ? 'Erneut versuchen' : 'Jetzt kostenlos prüfen';

    return (
      <div className="chat-container">
        <header className="chat-header">
          <img src="/logo.png" alt="" className="chat-header-logo" />
          <div className="chat-header-text">
            <h1>ZeKi</h1>
            <p>Baurechtliche Prüfung</p>
          </div>
        </header>

        <div className="start-screen">
          <h2>Kostenlose bauordnungsrechtliche Prüfung Ihrer Werbeanlage</h2>
          <p>
            Erfahren Sie in wenigen Minuten, ob Ihre geplante Werbeanlage
            verfahrensfrei oder genehmigungspflichtig ist.
          </p>
          <ul className="start-checklist">
            <li>Kostenlos</li>
            <li>Schnell</li>
            <li>Unverbindlich</li>
            <li>Nach Ihrer Landesbauordnung</li>
          </ul>
          {startError && (
            <p className="start-error" role="alert">{startError}</p>
          )}
          <button className="start-button" onClick={startChat} disabled={isLoading}>
            {isLoading ? 'Wird geladen...' : startLabel}
          </button>
          <p className="start-disclaimer">
            Mit dem Klick auf „{startLabel}" erklären Sie sich mit unserer{' '}
            <a href="https://werbeanlage-baugenehmigung.de/privacy-policy/" target="_blank" rel="noreferrer">
              Datenschutzerklärung
            </a>{' '}
            einverstanden.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-container">
      <header className="chat-header">
        <img src="/logo.png" alt="" className="chat-header-logo" />
        <div className="chat-header-text">
          <h1>ZeKi</h1>
          <p>Baurechtliche Prüfung</p>
        </div>
      </header>

      {isTesterMode && (
        <div className="tester-banner">
          Tester-Modus aktiv - Kommentare werden gespeichert
          {testerError && <span className="tester-banner-error">{testerError}</span>}
        </div>
      )}

      <div className="chat-messages" ref={messagesRef} role="log" aria-live="polite">
        {messages.map((message, index) => (
          <ChatMessage
            key={message.id}
            message={message}
            messageIndex={index}
            onButtonClick={handleButtonClick}
            isLast={index === messages.length - 1}
            isTesterMode={isTesterMode}
            testerComment={testerComments[String(index)]}
            onTesterComment={handleTesterComment}
          />
        ))}
        {isLoading && <TypingIndicator />}
      </div>

      <ChatInput
        onSubmit={(msg, meta) => sendMessage(msg, meta)}
        isLoading={isLoading}
        inputType={currentInputType}
        placeholder={currentPlaceholder}
        hasButtons={(currentButtons?.length || 0) > 0}
      />
    </div>
  );
}
